/**
 * PDF viewer + annotator (`/pdf`) — client-side pdf.js rendering with a
 * local-first annotation overlay.
 *
 * Open any local PDF (no upload: the FILE never leaves the browser, matching
 * the local-first/privacy stance), read it (pages, zoom) and ANNOTATE it:
 * highlight, note and freehand ink tools draw on a normalized SVG overlay.
 * The annotation set persists local-first keyed by the PDF's fingerprint
 * (page + `pdf-annotations` block: autosave/history/trash/sync reused — see
 * `../hooks/use-pdf-annotations.ts`); re-opening the same file re-attaches
 * them. "Export annotated" burns the overlay into a downloadable copy
 * (pdf-lib). Full PDF text-reflow editing remains an explicit non-goal
 * (OFFICE_FORMATS.md); the text-selection layer is a future increment.
 */
import { createEffect, createMemo, createSignal, For, onCleanup, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  FileUp,
  Highlighter,
  Loader2,
  MousePointer2,
  PenLine,
  StickyNote,
  ZoomIn,
  ZoomOut,
} from "@fleet/icons";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { cn } from "@folio/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { showToast } from "@/components/ui/toast";
import {
  addAnnotation,
  annotationsOnPage,
  removeAnnotation,
  updateNoteText,
} from "../lib/pdf-annotations";
import { downloadBlob, stripExtension } from "../lib/download";
import { usePdfAnnotations } from "../hooks/use-pdf-annotations";
import { PdfAnnotationLayer, type PdfTool } from "./pdf-annotation-layer";

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3] as const;
const DEFAULT_ZOOM_INDEX = 2; // 100%

async function loadPdfJs() {
  // `?url` works in BOTH vite dev (served from /@fs) and build (emitted
  // asset) — unlike `new URL(bare-specifier, import.meta.url)`, which the
  // dev server does not resolve (same class of gotcha as the Loro WASM).
  const [pdfjs, worker] = await Promise.all([
    import("pdfjs-dist"),
    import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return pdfjs;
}

export function PdfViewer() {
  const { t } = useI18n();
  const [doc, setDoc] = createSignal<PDFDocumentProxy | null>(null);
  const [pageNum, setPageNum] = createSignal(1);
  const [zoomIdx, setZoomIdx] = createSignal(DEFAULT_ZOOM_INDEX);
  const [loading, setLoading] = createSignal(false);
  const [fileName, setFileName] = createSignal("");
  const [fingerprint, setFingerprint] = createSignal<string | null>(null);
  const [tool, setTool] = createSignal<PdfTool>("select");
  const [exporting, setExporting] = createSignal(false);
  /** Copy of the opened file (pdf.js transfers ITS buffer to the worker). */
  let originalBytes: Uint8Array | null = null;
  let fileInput: HTMLInputElement | undefined;
  let canvas: HTMLCanvasElement | undefined;
  let renderSeq = 0;

  // Local-first annotation overlay, keyed by the file fingerprint.
  const annotations = usePdfAnnotations({ fingerprint, fileName });

  onCleanup(() => {
    void doc()?.loadingTask.destroy();
  });

  const handleOpenFile = async (file: File) => {
    setLoading(true);
    try {
      const pdfjs = await loadPdfJs();
      const data = new Uint8Array(await file.arrayBuffer());
      // Keep an intact copy for "Export annotated": pdf.js detaches `data`.
      const copy = data.slice();
      const nextDoc = await pdfjs.getDocument({ data }).promise;
      const prev = doc();
      originalBytes = copy;
      setDoc(nextDoc);
      setPageNum(1);
      setZoomIdx(DEFAULT_ZOOM_INDEX);
      setFileName(file.name);
      setTool("select");
      setFingerprint(nextDoc.fingerprints[0] ?? null);
      if (prev) {
        void prev.loadingTask.destroy();
      }
    } catch (err) {
      console.error("[pdf] open failed:", err);
      showToast(t(msg`Could not open that PDF`), "error");
    } finally {
      setLoading(false);
    }
  };

  const pageAnnotations = createMemo(() => {
    const model = annotations.model();
    return model ? annotationsOnPage(model, pageNum() - 1) : [];
  });

  const mutateModel = (
    mutate: (model: NonNullable<ReturnType<typeof annotations.model>>) => typeof model,
  ) => {
    const model = annotations.model();
    if (model) {
      annotations.update(mutate(model));
    }
  };

  const handleExportAnnotated = async () => {
    const model = annotations.model();
    if (!originalBytes || !model) {
      return;
    }
    setExporting(true);
    try {
      const { exportAnnotatedPdf } = await import("../lib/pdf-export");
      const out = await exportAnnotatedPdf(originalBytes, model.annotations);
      const buffer = new ArrayBuffer(out.byteLength);
      new Uint8Array(buffer).set(out);
      downloadBlob(
        new Blob([buffer], { type: "application/pdf" }),
        `${stripExtension(fileName() || "document")}-annotated.pdf`,
      );
    } catch (err) {
      console.error("[pdf] export failed:", err);
      showToast(t(msg`Could not export the annotated PDF`), "error");
    } finally {
      setExporting(false);
    }
  };

  const TOOLS: { id: PdfTool; icon: typeof MousePointer2; label: () => string }[] = [
    // i18n-reactive-ok: labels re-evaluated on render via accessor
    { id: "select", icon: MousePointer2, label: () => t(msg`Select`) },
    { id: "highlight", icon: Highlighter, label: () => t(msg`Highlight`) },
    { id: "note", icon: StickyNote, label: () => t(msg`Note`) },
    { id: "ink", icon: PenLine, label: () => t(msg`Draw`) },
  ];

  // Render the current page whenever doc/page/zoom change. A monotonically
  // increasing sequence discards stale async renders (fast page flipping).
  createEffect(() => {
    const pdf = doc();
    const num = pageNum();
    const scale = ZOOM_STEPS[zoomIdx()] ?? 1;
    if (!pdf || !canvas) {
      return;
    }
    const seq = ++renderSeq;
    void (async () => {
      try {
        const page = await pdf.getPage(num);
        if (seq !== renderSeq || !canvas) {
          return;
        }
        const dpr = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: scale * dpr });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width / dpr}px`;
        canvas.style.height = `${viewport.height / dpr}px`;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return;
        }
        await page.render({ canvas, canvasContext: ctx, viewport }).promise;
        if (seq === renderSeq) {
          canvas.dataset.rendered = `${num}@${scale}`;
        }
      } catch (err) {
        // Rendering a page that got superseded throws; only real errors matter.
        if (seq === renderSeq) {
          console.error("[pdf] render failed:", err);
        }
      }
    })();
  });

  const pageCount = () => doc()?.numPages ?? 0;

  return (
    <div class="flex h-full min-h-0 flex-col gap-3 p-4" data-testid="pdf-viewer">
      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div class="flex flex-wrap items-center gap-2">
        <div class="mr-auto flex min-w-0 items-center gap-2">
          <FileText class="h-5 w-5 shrink-0 text-primary" />
          <span class="truncate text-sm font-medium" data-testid="pdf-file-name">
            {fileName() || t(msg`PDF viewer`)}
          </span>
        </div>
        <input
          ref={(el) => (fileInput = el)}
          type="file"
          accept=".pdf,application/pdf"
          class="hidden"
          data-testid="pdf-open-input"
          onChange={(e) => {
            const file = e.currentTarget.files?.[0];
            if (file) {
              void handleOpenFile(file);
            }
            e.currentTarget.value = "";
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          class="gap-1.5"
          data-testid="pdf-open"
          disabled={loading()}
          onClick={() => fileInput?.click()}
        >
          <Show when={loading()} fallback={<FileUp class="h-4 w-4" />}>
            <Loader2 class="h-4 w-4 animate-spin" />
          </Show>
          {t(msg`Open PDF`)}
        </Button>
        <Show when={doc()}>
          {/* ── Annotation tools ── */}
          <div class="flex items-center gap-0.5 rounded-lg border border-border/50 p-0.5">
            <For each={TOOLS}>
              {(entry) => (
                <Button
                  variant="ghost"
                  size="sm"
                  class={cn("h-8 w-8 p-0", tool() === entry.id && "bg-accent/15 text-accent")}
                  data-testid={`pdf-tool-${entry.id}`}
                  aria-pressed={tool() === entry.id}
                  aria-label={entry.label()}
                  title={entry.label()}
                  onClick={() => setTool(entry.id)}
                >
                  <entry.icon class="h-4 w-4" />
                </Button>
              )}
            </For>
          </div>
          {/* ── Autosave state of the annotation overlay ── */}
          <Show
            when={annotations.saveError()}
            fallback={
              <Show
                when={annotations.isSaving()}
                fallback={
                  <Show when={annotations.lastSavedAt()}>
                    <span
                      class="flex shrink-0 items-center gap-1 text-xs font-medium text-success"
                      aria-live="polite"
                      data-testid="pdf-saved"
                    >
                      <Check class="h-3.5 w-3.5" />
                      <span class="hidden sm:inline">{t(msg`Saved`)}</span>
                    </span>
                  </Show>
                }
              >
                <span class="flex shrink-0 items-center gap-1 text-xs text-muted-foreground/60">
                  <Loader2 class="h-3.5 w-3.5 animate-spin" />
                </span>
              </Show>
            }
          >
            <span
              class="flex shrink-0 items-center gap-1 text-xs font-medium text-danger"
              role="status"
              data-testid="pdf-save-failed"
            >
              <AlertCircle class="h-3.5 w-3.5" />
            </span>
          </Show>
          <Button
            variant="ghost"
            size="sm"
            class="gap-1.5"
            data-testid="pdf-export-annotated"
            disabled={exporting()}
            onClick={() => {
              void handleExportAnnotated();
            }}
            aria-label={t(msg`Export annotated PDF`)}
            title={t(msg`Export annotated PDF`)}
          >
            <Show when={exporting()} fallback={<Download class="h-4 w-4" />}>
              <Loader2 class="h-4 w-4 animate-spin" />
            </Show>
            <span class="hidden md:inline">{t(msg`Export`)}</span>
          </Button>
          <div class="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              class="h-8 w-8 p-0"
              data-testid="pdf-zoom-out"
              aria-label={t(msg`Zoom out`)}
              disabled={zoomIdx() === 0}
              onClick={() => setZoomIdx((i) => Math.max(0, i - 1))}
            >
              <ZoomOut class="h-4 w-4" />
            </Button>
            <span class="w-12 text-center text-xs tabular-nums text-muted-foreground">
              {Math.round((ZOOM_STEPS[zoomIdx()] ?? 1) * 100)}%
            </span>
            <Button
              variant="ghost"
              size="sm"
              class="h-8 w-8 p-0"
              data-testid="pdf-zoom-in"
              aria-label={t(msg`Zoom in`)}
              disabled={zoomIdx() === ZOOM_STEPS.length - 1}
              onClick={() => setZoomIdx((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
            >
              <ZoomIn class="h-4 w-4" />
            </Button>
          </div>
          <div class="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              class="h-8 w-8 p-0"
              data-testid="pdf-prev-page"
              aria-label={t(msg`Previous page`)}
              disabled={pageNum() <= 1}
              onClick={() => setPageNum((n) => Math.max(1, n - 1))}
            >
              <ChevronLeft class="h-4 w-4" />
            </Button>
            <span class="text-xs tabular-nums text-muted-foreground" data-testid="pdf-page-label">
              {pageNum()} / {pageCount()}
            </span>
            <Button
              variant="ghost"
              size="sm"
              class="h-8 w-8 p-0"
              data-testid="pdf-next-page"
              aria-label={t(msg`Next page`)}
              disabled={pageNum() >= pageCount()}
              onClick={() => setPageNum((n) => Math.min(pageCount(), n + 1))}
            >
              <ChevronRight class="h-4 w-4" />
            </Button>
          </div>
        </Show>
      </div>

      {/* ── Canvas surface ──────────────────────────────────────── */}
      <div class="min-h-0 flex-1 overflow-auto rounded-lg border border-border/50 bg-card/30 p-4">
        <Show
          when={doc()}
          fallback={
            <EmptyState
              icon={FileText}
              title={t(msg`No PDF open`)}
              description={t(
                msg`Open a PDF to read it here. Files are rendered locally and never uploaded.`,
              )}
              action={
                <Button data-testid="pdf-open-empty" onClick={() => fileInput?.click()}>
                  {t(msg`Open PDF`)}
                </Button>
              }
            />
          }
        >
          <div class="flex justify-center">
            <div class="relative">
              <canvas
                ref={(el) => (canvas = el)}
                data-testid="pdf-canvas"
                class="rounded-md bg-white shadow-lg"
              />
              <Show when={annotations.model()}>
                <PdfAnnotationLayer
                  annotations={pageAnnotations}
                  pageIndex={() => pageNum() - 1}
                  tool={tool}
                  onAdd={(annotation) => mutateModel((model) => addAnnotation(model, annotation))}
                  onUpdateNote={(id, text) =>
                    mutateModel((model) => updateNoteText(model, id, text))
                  }
                  onRemove={(id) => mutateModel((model) => removeAnnotation(model, id))}
                />
              </Show>
            </div>
          </div>
        </Show>
      </div>
    </div>
  );
}
